import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useProductEditor } from '@/hooks/useProductEditor';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Upload, Eye } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface PublishButtonProps {
  productId: string;
  isPublished: boolean;
}

export const PublishButton = ({ productId, isPublished }: PublishButtonProps) => {
  const { isAdmin } = useAuth();
  const { publishProduct, isPublishing } = useProductEditor();
  const [open, setOpen] = useState(false);

  if (!isAdmin) {
    return null;
  }

  const handlePublish = () => {
    publishProduct(productId);
    setOpen(false);
  };

  return (
    <div className="fixed bottom-8 right-8 z-50 flex items-center gap-3">
      <Badge variant={isPublished ? 'default' : 'secondary'} className="text-sm py-2 px-3">
        {isPublished ? (
          <>
            <Eye className="h-3 w-3 mr-1" />
            Published
          </>
        ) : (
          <>
            <Eye className="h-3 w-3 mr-1" />
            Draft
          </>
        )}
      </Badge>

      {!isPublished && (
        <AlertDialog open={open} onOpenChange={setOpen}>
          <AlertDialogTrigger asChild>
            <Button
              size="lg"
              className="shadow-lg bg-green-600 hover:bg-green-700"
            >
              <Upload className="h-4 w-4 mr-2" />
              Publish Product
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Publish this product?</AlertDialogTitle>
              <AlertDialogDescription>
                This will make the product visible to all users on the site. Make sure all
                content is accurate and complete before publishing.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handlePublish}
                disabled={isPublishing}
                className="bg-green-600 hover:bg-green-700"
              >
                {isPublishing ? 'Publishing...' : 'Publish'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
};
